import React from "react";
import DashboardPresenter from "./DashboardPresenter";
import NavBar from "../../components/navbar";
import BaseListPage from "../../base/BaseListPage";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../usecases/object";
import { dialog, Button } from "../../nq-component";
import FormCollection from "../collection-list/components/FormCollection";
import { updateSchemaUseCase } from "../../usecases/schema/usecases";
import InquiryTrendChart from "./components/InquiryTrendChart";
import ComparisonChart from "./components/ComparisonChart";
import StatusChart from "./components/StatusChart";

class DashboardPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new DashboardPresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      upsertUseCase(),
      updateSchemaUseCase(),
    );
    this.state = {
      objects: [],
      selected: [],
      progress: true,
      count: 0,
      where: {},
      trendDateFilter: { start: "", end: "" },
      comparisonDateFilter: { start: "", end: "" },
      showTrendDateFilter: false,
      showComparisonDateFilter: false,
      trendMonthlyData: null,
      comparisonMonthlyData: null,
    };
  }

  getCollectionName() {
    return "inquiries";
  }

  closeDialog() {
    dialog.close();
  }

  onClickEditCollection() {
    const schema = this.getSchema(this.getCollectionName());
    dialog.fire({
      html: (
        <FormCollection
          schema={schema}
          onSubmit={(s) => this.presenter.onSubmitEditCollection(s)}
          onCancel={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }

  onChangeFilter(type, value, field) {
    const where = {};
    switch (type) {
      case "Pointer":
        if (Object.keys(value).length > 0) {
          where[field] = { id: value.id };
        }
        break;
      case "Boolean":
        where[field] = value;
        break;
      default:
        where[field] = { $regex: value, $options: "i" };
    }
    this.setState({ where });
  }

  toggleTrendDateFilter = () => {
    this.setState((prev) => ({
      showTrendDateFilter: !prev.showTrendDateFilter,
    }));
  };

  toggleComparisonDateFilter = () => {
    this.setState((prev) => ({
      showComparisonDateFilter: !prev.showComparisonDateFilter,
    }));
  };

  handleTrendDateChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prev) => ({
        trendDateFilter: { ...prev.trendDateFilter, [name]: value },
      }),
      this.filterTrendData,
    );
  };

  handleComparisonDateChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prev) => ({
        comparisonDateFilter: { ...prev.comparisonDateFilter, [name]: value },
      }),
      this.filterComparisonData,
    );
  };

  filterTrendData = () => {
    const { trendDateFilter, objects } = this.state;
    const { start, end } = trendDateFilter;
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;

    const filtered = objects.filter((obj) => {
      const created = new Date(obj.created);
      return (
        (!startDate || created >= startDate) && (!endDate || created <= endDate)
      );
    });

    this.setState({ trendMonthlyData: this.computeMonthlyData(filtered) });
  };

  filterComparisonData = () => {
    const { comparisonDateFilter, objects } = this.state;
    const { start, end } = comparisonDateFilter;
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;

    const filtered = objects.filter((obj) => {
      const created = new Date(obj.created);
      return (
        (!startDate || created >= startDate) && (!endDate || created <= endDate)
      );
    });

    this.setState({ comparisonMonthlyData: this.computeMonthlyData(filtered) });
  };

  computeMonthlyData = (data) => {
    const monthlyTotalInquiries = Array(12).fill(0);
    const monthlyClosedDeals = Array(12).fill(0);
    const monthlyWarmLeads = Array(12).fill(0);
    const monthlyColdLeads = Array(12).fill(0);

    data.forEach((obj) => {
      const monthIndex = new Date(obj.created).getMonth();
      monthlyTotalInquiries[monthIndex]++;
      if (obj.status === "Closed Deal") monthlyClosedDeals[monthIndex]++;
      if (obj.status === "Pending (Warm Lead)") monthlyWarmLeads[monthIndex]++;
      if (obj.status === "Pending (Cold Lead)") monthlyColdLeads[monthIndex]++;
    });

    return {
      totalInquiries: monthlyTotalInquiries,
      closedDeals: monthlyClosedDeals,
      warmLeads: monthlyWarmLeads,
      coldLeads: monthlyColdLeads,
    };
  };

  render() {
    const schema = this.getSchema(this.getCollectionName());
    const {
      objects,
      trendMonthlyData,
      comparisonMonthlyData,
      showTrendDateFilter,
      showComparisonDateFilter,
      trendDateFilter,
      comparisonDateFilter,
    } = this.state;
    if (!schema) return null;
    const clientCount = objects.filter((obj) => obj.client).length;
    const warmCount = objects.filter(
      (obj) => obj.status === "Pending (Warm Lead)",
    ).length;
    const coldCount = objects.filter(
      (obj) => obj.status === "Pending (Cold Lead)",
    ).length;
    const cancelCount = objects.filter(
      (obj) => obj.status === "Cancelled",
    ).length;
    const closedCount = objects.filter(
      (obj) => obj.status === "Closed Deal",
    ).length;

    const monthlyData = this.computeMonthlyData(objects);

    const statusCounts = {
      Draft: 0,
      "Pending (Warm Lead)": 0,
      "Pending (Cold Lead)": 0,
      "Closed Deal": 0,
      Cancelled: 0,
    };

    objects.forEach((obj) => {
      if (statusCounts[obj.status] !== undefined) {
        statusCounts[obj.status]++;
      }
    });

    const user = this.getCurrentUser();
    return (
      <>
        <NavBar
          action={() => {
            if (!user.isMaster) return null;
            return (
              <div className="dropdown dropstart d-inline-block">
                <i
                  role="button"
                  data-bs-toggle="dropdown"
                  className="bi bi-three-dots-vertical"
                ></i>
                <div className="dropdown-menu fs-xs"></div>
              </div>
            );
          }}
        />
        <div className="overflow-auto">
          <div className="p-3 p-lg-4">
            <h1 className="fw-bold mt-3 text-capitalize">Dashboard</h1>
            <div className="row mt-1 g-3">
              <div>
                {/* <div className="d-flex gap-3 justify-content-center">
                  <div className="bg-white border border-gray rounded px-3 pt-2 w-100">
                    <div className="d-flex justify-content-between">
                      <h3 className="fw-bolder">{coldCount}</h3>
                      <i
                        className="bi bi-snow text-primary"
                        style={{ fontSize: "25px" }}
                      ></i>
                    </div>
                    <p className="mt-1">Total Cold Lead Inquiries</p>
                  </div>
                  <div className="bg-white border border-gray rounded px-3 pt-2 w-100">
                    <div className="d-flex justify-content-between">
                      <h3 className="fw-bolder">{warmCount}</h3>
                      <i
                        className="bi bi-hourglass-split text-primary"
                        style={{ fontSize: "25px" }}
                      ></i>
                    </div>
                    <p className="mt-1">Total Warm Lead Inquiries</p>
                  </div>
                </div>
                <div className="mt-2 bg-white border border-gray rounded p-3 py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <i
                        className="bi bi-person-video2 text-primary"
                        style={{ fontSize: "25px" }}
                      ></i>
                      <p className="mb-0">Total Clients</p>
                    </div>
                    <h5 className="fw-bolder mb-0">{clientCount}</h5>
                  </div>
                </div>
                <div className="mt-2 bg-white border border-gray rounded p-3 py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <i
                        className="bi bi-check-circle-fill text-primary"
                        style={{ fontSize: "25px" }}
                      ></i>
                      <p className="mb-0">Total Closed Deal</p>
                    </div>
                    <h5 className="fw-bolder mb-0">{closedCount}</h5>
                  </div>
                </div>
                <div className="mt-2 bg-white border border-gray rounded p-3 py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <i
                        className="bi bi-x-circle-fill text-primary"
                        style={{ fontSize: "25px" }}
                      ></i>
                      <p className="mb-0">Total Cancelled Inquiries</p>
                    </div>
                    <h5 className="fw-bolder mb-0">{cancelCount}</h5>
                  </div>
                </div> */}
                <div className="row g-3">
                  {/* Featured stat */}
                  <div className="mb-3">
                    {/* <h2 className="fw-bolder mb-1">Sales Overview</h2> */}
                    <p className="text-muted mb-0">
                      Track your lead pipeline and conversion performance
                    </p>
                  </div>
                  <div className="col-12">
                    <div
                      className="rounded-4 p-4 text-white position-relative overflow-hidden"
                      style={{
                        background:
                          "linear-gradient(135deg, #0d6efd 0%, #4f8cff 55%, #7ab8ff 100%)",
                        boxShadow: "0 10px 30px rgba(13,110,253,0.25)",
                      }}
                    >
                      <div
                        className="position-absolute"
                        style={{
                          top: "-30px",
                          right: "-20px",
                          width: "140px",
                          height: "140px",
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.12)",
                        }}
                      />
                      <div className="d-flex justify-content-between align-items-center position-relative">
                        <div>
                          <p
                            className="mb-2 text-uppercase fw-semibold"
                            style={{ letterSpacing: "1px", opacity: 0.85 }}
                          >
                            Total Clients
                          </p>
                          <h1
                            className="fw-bolder mb-1"
                            style={{ fontSize: "2.5rem" }}
                          >
                            {clientCount}
                          </h1>
                          <small style={{ opacity: 0.9 }}>
                            Active clients in your pipeline
                          </small>
                        </div>

                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: "72px",
                            height: "72px",
                            borderRadius: "20px",
                            background: "rgba(255,255,255,0.18)",
                            backdropFilter: "blur(6px)",
                          }}
                        >
                          <i
                            className="bi bi-person-video2"
                            style={{ fontSize: "34px", color: "#fff" }}
                          ></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cold + Warm */}
                  <div className="col-md-6">
                    <div
                      className="bg-white rounded-4 p-4 h-100 border-0"
                      style={{ boxShadow: "0 8px 24px rgba(16,24,40,0.08)" }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <p className=" mb-2 fw-semibold">Cold Leads</p>
                          <h2 className="fw-bolder mb-1">{coldCount}</h2>
                          <small className="text-muted">
                            {/* New or unengaged inquiries */}
                            Inactive (greater than 7 days without updates)
                          </small>
                        </div>
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: "54px",
                            height: "54px",
                            borderRadius: "16px",
                            background: "rgba(13,110,253,0.1)",
                          }}
                        >
                          <i
                            className="bi bi-snow text-primary"
                            style={{ fontSize: "24px" }}
                          ></i>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div
                          className="rounded-pill"
                          style={{
                            height: "8px",
                            background: "#e9f2ff",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(coldCount * 10, 100)}%`,
                              height: "100%",
                              background:
                                "linear-gradient(90deg, #0d6efd, #6ea8fe)",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div
                      className="bg-white rounded-4 p-4 h-100 border-0"
                      style={{ boxShadow: "0 8px 24px rgba(16,24,40,0.08)" }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <p className=" mb-2 fw-semibold">Warm Leads</p>
                          <h2 className="fw-bolder mb-1">{warmCount}</h2>
                          <small className="text-muted">
                            Recently engaged (≤ 7 days)
                          </small>
                        </div>
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: "54px",
                            height: "54px",
                            borderRadius: "16px",
                            background: "rgba(255,193,7,0.12)",
                          }}
                        >
                          <i
                            className="bi bi-hourglass-split text-warning"
                            style={{ fontSize: "24px" }}
                          ></i>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div
                          className="rounded-pill"
                          style={{
                            height: "8px",
                            background: "#fff4d6",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(warmCount * 10, 100)}%`,
                              height: "100%",
                              background:
                                "linear-gradient(90deg, #ffc107, #ffda6a)",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom cards */}
                  <div className="col-md-6 col-xl-4">
                    <div
                      className="bg-white rounded-4 p-3 h-100"
                      style={{ boxShadow: "0 8px 24px rgba(16,24,40,0.08)" }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "16px",
                              background: "rgba(25,135,84,0.12)",
                            }}
                          >
                            <i
                              className="bi bi-check-circle-fill text-success"
                              style={{ fontSize: "22px" }}
                            ></i>
                          </div>
                          <div>
                            <p className="mb-0 text-muted fw-semibold">
                              Closed Sale
                            </p>
                            <small className="text-muted">
                              Successfully converted
                            </small>
                          </div>
                        </div>
                        <h3 className="fw-bolder mb-0">{closedCount}</h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-xl-4">
                    <div
                      className="bg-white rounded-4 p-3 h-100"
                      style={{ boxShadow: "0 8px 24px rgba(16,24,40,0.08)" }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "16px",
                              background: "rgba(220,53,69,0.12)",
                            }}
                          >
                            <i
                              className="bi bi-x-circle-fill text-danger"
                              style={{ fontSize: "22px" }}
                            ></i>
                          </div>
                          <div>
                            <p className="mb-0 text-muted fw-semibold">
                              Cancelled Inquiries
                            </p>
                            <small className="text-muted">
                              Dropped or rejected leads
                            </small>
                          </div>
                        </div>
                        <h3 className="fw-bolder mb-0">{cancelCount}</h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12 col-xl-4">
                    <div
                      className="rounded-4 p-3 h-100 text-white"
                      style={{
                        background:
                          "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
                        boxShadow: "0 8px 24px rgba(17,24,39,0.18)",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p
                            className="mb-1 fw-semibold"
                            style={{ opacity: 0.8 }}
                          >
                            Conversion Snapshot
                          </p>
                          <h3 className="fw-bolder mb-0">{closedCount}</h3>
                          <small style={{ opacity: 0.75 }}>
                            Deals closed so far
                          </small>
                        </div>
                        <i
                          className="bi bi-graph-up-arrow"
                          style={{ fontSize: "30px" }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
                <h6 className="fw-bold mt-4 mb-1 text-capitalize">
                  Inquiry Trends Over Time
                </h6>
                <div className="mt-3 bg-white p-3 rounded border">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        {showTrendDateFilter ? (
                          <>
                            {/* {!trendDateFilter.start ? (  */}
                            <div className="form-floating">
                              <input
                                type="date"
                                id="startDate"
                                name="start"
                                value={trendDateFilter.start}
                                onChange={this.handleTrendDateChange}
                                className="form-control form-control-sm"
                              />
                              <label
                                className="text-primary"
                                htmlFor="startDate"
                              >
                                Select Start Date
                              </label>
                            </div>
                            {/* ) : ( */}
                            <div className="form-floating">
                              <input
                                type="date"
                                id="endDate"
                                name="end"
                                value={trendDateFilter.end}
                                onChange={this.handleTrendDateChange}
                                className="form-control form-control-sm"
                              />
                              <label className="text-primary" htmlFor="endDate">
                                Select End Date
                              </label>
                            </div>
                            {/* )} */}
                            <Button
                              className="btn btn-sm btn-primary d-flex align-items-center justify-content-center"
                              style={{
                                width: "25px",
                                height: "25px",
                                borderRadius: "50%",
                              }}
                              onClick={() =>
                                this.setState({
                                  trendMonthlyData: null,
                                  trendDateFilter: { start: "", end: "" },
                                  showTrendDateFilter: false,
                                })
                              }
                            >
                              <i className="bi bi-x-lg" />
                            </Button>
                          </>
                        ) : (
                          <i
                            className="bi bi-filter text-primary"
                            onClick={this.toggleTrendDateFilter}
                            style={{ cursor: "pointer" }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-column align-items-end">
                    <div className="d-flex align-items-center mb-1">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: "#00d0dc",
                          marginRight: 6,
                        }}
                      ></div>
                      <span style={{ fontSize: 12 }}>Total Inquiries</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: "#0073a5",
                          marginRight: 6,
                        }}
                      ></div>
                      <span style={{ fontSize: 12 }}>Closed Deal</span>
                    </div>
                  </div>
                  <InquiryTrendChart
                    monthlyData={trendMonthlyData || monthlyData}
                  />
                </div>
                <div className="mt-3 bg-white p-3 rounded border">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        {showComparisonDateFilter ? (
                          <>
                            {!comparisonDateFilter.start ? (
                              <div className="form-floating">
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  id="startDate"
                                  name="start"
                                  value={comparisonDateFilter.start}
                                  onChange={this.handleComparisonDateChange}
                                />
                                <label
                                  className="text-primary"
                                  htmlFor="startDate"
                                >
                                  Select Start Date
                                </label>
                              </div>
                            ) : (
                              <div className="form-floating">
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  id="endDate"
                                  name="end"
                                  value={comparisonDateFilter.end}
                                  onChange={this.handleComparisonDateChange}
                                />
                                <label
                                  className="text-primary"
                                  htmlFor="endDate"
                                >
                                  Select End Date
                                </label>
                              </div>
                            )}
                            <Button
                              className="btn btn-sm btn-primary d-flex align-items-center justify-content-center"
                              style={{
                                width: "25px",
                                height: "25px",
                                borderRadius: "50%",
                              }}
                              onClick={() =>
                                this.setState({
                                  comparisonMonthlyData: null,
                                  comparisonDateFilter: { start: "", end: "" },
                                  showComparisonDateFilter: false,
                                })
                              }
                            >
                              <i className="bi bi-x-lg" />
                            </Button>
                          </>
                        ) : (
                          <i
                            className="bi bi-filter text-primary"
                            onClick={this.toggleComparisonDateFilter}
                            style={{ cursor: "pointer" }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <ComparisonChart
                    monthlyData={comparisonMonthlyData || monthlyData}
                  />
                </div>
                <h6 className="fw-bold mt-4 mb-1 text-capitalize">
                  Inquiry Status Distribution
                </h6>
                <div className="mt-3 bg-white p-3 rounded border">
                  <div className="d-flex justify-content-center align-items-start mb-2">
                    <StatusChart statusCounts={statusCounts} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default DashboardPage;
